import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFab from "../components/WhatsAppFab";
import { useI18n } from "../i18n/I18nContext";
import { useSEO } from "../lib/seo";
import api from "../lib/api";
import { Send, MessageCircle, Loader2, Check } from "lucide-react";
import { WHATSAPP_HREF } from "../components/WhatsAppFab";

const EMPTY = {
  establishment: "", contactName: "", email: "", phone: "", city: "",
  establishmentType: "", surface: "", desiredNeed: "", frequencyComment: "", message: "",
};

const buildWaMessage = (q, form) => {
  const L = q.waLabels;
  let txt = q.waPrefix;
  txt += `${L.establishment} : ${form.establishment}\n`;
  txt += `${L.contactName} : ${form.contactName}\n`;
  txt += `${L.phone} : ${form.phone}\n`;
  txt += `${L.email} : ${form.email}\n`;
  txt += `${L.city} : ${form.city}\n`;
  txt += `${L.establishmentType} : ${form.establishmentType}\n`;
  txt += `${L.surface} : ${form.surface ? form.surface + " m²" : "—"}\n`;
  txt += `${L.desiredNeed} : ${form.desiredNeed}\n`;
  txt += `${L.frequencyComment} : ${form.frequencyComment || "—"}\n`;
  txt += `${L.message} : ${form.message || "—"}\n`;
  return txt;
};

export default function QuoteRequest() {
  const { locale, t, routes } = useI18n();
  const q = t.quote;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [waUrl, setWaUrl] = useState("");

  useSEO({ title: `${q.pageTitle} · SAFEMAT`, description: q.lead, locale });

  const u = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/inquiries", {
        ...form,
        surface: form.surface ? Number(form.surface) : null,
        locale,
      });
      const url = `${WHATSAPP_HREF}?text=${encodeURIComponent(buildWaMessage(q, form))}`;
      setWaUrl(url);
      setStatus("sent");
      // Open WhatsApp
      const w = window.open(url, "_blank");
      if (!w) {
        // popup blocked — user will use the button on the success screen
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div data-testid="quote-page">
      <Header />
      <div className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-24 pb-12 lg:pt-32 lg:pb-16">
          <p className="eyebrow text-slate-400 mb-4">{q.eyebrow}</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl">
            {q.title}
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">{q.lead}</p>
        </div>
      </div>

      <main className="bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 section">
          {status !== "sent" ? (
            <form onSubmit={submit} className="card p-6 lg:p-8 space-y-4" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={q.fields.establishment} value={form.establishment} onChange={u("establishment")} required testid="qf-establishment" />
                <Field label={q.fields.contactName} value={form.contactName} onChange={u("contactName")} required testid="qf-contactName" />
                <Field label={q.fields.email} type="email" value={form.email} onChange={u("email")} required testid="qf-email" />
                <Field label={q.fields.phone} value={form.phone} onChange={u("phone")} required testid="qf-phone" />
                <Field label={q.fields.city} value={form.city} onChange={u("city")} required testid="qf-city" />
                <Field label={q.fields.surface} type="number" value={form.surface} onChange={u("surface")} testid="qf-surface" />
                <Select label={q.fields.establishmentType} value={form.establishmentType} onChange={u("establishmentType")} options={q.typeOptions} required testid="qf-type" />
                <Select label={q.fields.desiredNeed} value={form.desiredNeed} onChange={u("desiredNeed")} options={q.needOptions} required testid="qf-need" />
              </div>
              <div>
                <label className="field-label">{q.fields.frequencyComment}</label>
                <textarea
                  value={form.frequencyComment}
                  onChange={u("frequencyComment")}
                  rows={3}
                  placeholder={q.frequencyPlaceholder}
                  className="field"
                  data-testid="qf-frequency-comment"
                />
              </div>
              <div>
                <label className="field-label">{q.fields.message}</label>
                <textarea value={form.message} onChange={u("message")} rows={4} className="field" data-testid="qf-message" />
              </div>
              <div className="pt-2">
                <button type="submit" disabled={status === "sending"} className="btn-primary w-full sm:w-auto" data-testid="qf-submit">
                  {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {status === "sending" ? q.sending : q.submit}
                </button>
                {status === "error" && (
                  <p className="mt-3 text-sm text-[#016C36]">{q.error}</p>
                )}
              </div>
            </form>
          ) : (
            <div className="card p-8 lg:p-10" data-testid="quote-success">
              <div className="w-14 h-14 bg-emerald-600 rounded-md flex items-center justify-center mb-6">
                <Check size={28} className="text-white" strokeWidth={3} />
              </div>
              <p className="eyebrow text-emerald-700 mb-3">OK</p>
              <h2 className="font-display font-bold text-3xl tracking-tight">{q.successTitle}</h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{q.successLead}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={waUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold rounded text-sm transition-colors"
                  data-testid="quote-wa-open">
                  <MessageCircle size={18} strokeWidth={2.2} /> {q.openWhatsApp}
                </a>
                <Link to={routes.home} className="btn-secondary">{q.back}</Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="field-label">{label}</label>
    <input type={type} value={value} onChange={onChange} required={required} className="field" data-testid={testid} />
  </div>
);

const Select = ({ label, value, onChange, options, required, testid }) => (
  <div>
    <label className="field-label">{label}</label>
    <select value={value} onChange={onChange} required={required} className="field" data-testid={testid}>
      <option value="">—</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
